/* eslint-disable sonarjs/cognitive-complexity */
import { BLOCKS, Block, INLINES, Inline } from '@contentful/rich-text-types';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { THEME_CLASSNAMES } from '@boss/constants/corporate-jobs';
import { CHANNEL } from '@boss/constants/shared';
import { InlineHyperlink } from '@boss/rich-text';
import type { RichTextFields, ThemeColor } from '@boss/types/corporate-jobs';
import { List } from '@boss/ui';

import ComponentMapper from '../ComponentMapper';

const getRichTextProps = (
  entry: RichTextFields,
  locale: string,
  themeColor: ThemeColor,
  isInColored2ColumnsSection?: boolean,
) => {
  const { alignment } = entry;
  const alignmentClassName = alignment === 'center' ? 'text-center' : '';
  const listAlignmentClassName = alignment === 'center' ? 'flex flex-col items-center' : '';
  const backgroundColorClassName = isInColored2ColumnsSection ? 'text-white' : '';

  const textColorClassName = () => {
    if (isInColored2ColumnsSection) {
      return 'text-white';
    }
    if (themeColor) {
      return THEME_CLASSNAMES.text[themeColor];
    }
    return '';
  };

  const bulletColorClassName = () => {
    if (isInColored2ColumnsSection) {
      return 'marker:text-white';
    }
    if (themeColor) {
      return THEME_CLASSNAMES.bullet[themeColor];
    }
    return '';
  };

  const childrenMapper = (children: ReactNode) => {
    return React.Children.map(children, child =>
      React.isValidElement(child)
        ? React.cloneElement(child as React.ReactElement<{ className?: string }>, {
            className: twMerge((child as React.ReactElement).props.className, 'my-2'),
          })
        : child,
    );
  };

  return {
    className: twMerge(backgroundColorClassName, alignmentClassName),
    content: entry.content,
    ComponentMapper,
    mapperOptions: {
      locale,
      parentIsColored2ColumnsSection: isInColored2ColumnsSection,
      alignment,
    },
    renderNode: {
      [BLOCKS.HEADING_2]: (_: Block | Inline, children: ReactNode) => (
        <h2 className={twMerge('mb-4', textColorClassName())}>{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (_: Block | Inline, children: ReactNode) => (
        <h3 className={twMerge('mb-4', textColorClassName())}>{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (_: Block | Inline, children: ReactNode) => (
        <h4 className={twMerge('mb-4', textColorClassName())}>{children}</h4>
      ),
      [BLOCKS.OL_LIST]: (_: Block | Inline, children: ReactNode) => (
        <List as="ordered" className={listAlignmentClassName}>
          {children}
        </List>
      ),
      [BLOCKS.UL_LIST]: (_: Block | Inline, children: ReactNode) =>
        CHANNEL !== 'corporate' ? (
          <ul className={twMerge('w-full list-none text-left', listAlignmentClassName)}>{children}</ul>
        ) : (
          <List className={listAlignmentClassName}>{children}</List>
        ),
      [BLOCKS.LIST_ITEM]: (_: Block | Inline, children: ReactNode) =>
        CHANNEL !== 'corporate' ? (
          <li className="flex items-start justify-self-start">
            <FontAwesomeIcon
              className={twMerge('text-brand mr-4 mt-4 flex-shrink-0', textColorClassName())}
              icon={faArrowRight}
              width={35}
            />
            {childrenMapper(children)}
          </li>
        ) : (
          <li className={bulletColorClassName()}>{childrenMapper(children)}</li>
        ),
      [INLINES.HYPERLINK]: (node: Block | Inline) => (
        <InlineHyperlink className={twMerge(textColorClassName())} hyperlink={node as Inline} />
      ),
    },
  };
};

export default getRichTextProps;
